/**
 * 语音配置 API 服务
 * v6.0 新增：ASR/TTS 提供商和音色配置
 * 调用 axiom-agent-admin 服务
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'

/**
 * ASR 提供商
 */
export interface ASRProvider {
  id: string
  name: string
  description: string
  supported_languages: string[]
  default: boolean
}

/**
 * ASR 提供商列表响应
 */
export interface ASRProvidersResponse {
  default_provider: string
  providers: ASRProvider[]
}

/**
 * TTS 提供商
 */
export interface TTSProvider {
  id: string
  name: string
  description: string
  default: boolean
}

/**
 * TTS 提供商列表响应
 */
export interface TTSProvidersResponse {
  default_provider: string
  providers: TTSProvider[]
}

/**
 * TTS 音色
 */
export interface TTSVoice {
  id: string
  name: string
  description: string
  gender: 'male' | 'female' | 'neutral'
  default: boolean
  // v6.0 扩展字段（可选，向后兼容）
  age_range?: string              // 年龄范围，如 "20~30岁"
  languages?: string[]            // 支持语言，如 ["中文（普通话）", "英文"]
  scenarios?: string[]            // 适用场景，如 ["社交陪伴", "语音助手"]
  features?: {                    // 特性支持（前端不展示，仅供参考）
    ssml: boolean
    instruct: boolean
    timestamp: boolean
  }
}

/**
 * 音频格式
 */
export interface AudioFormat {
  id: string
  name: string
  description: string
  default: boolean
}

/**
 * TTS 音色列表响应
 */
export interface TTSVoicesResponse {
  // v6.0 扩展字段（可选）
  version?: string              // 配置版本，如 "1.0"
  last_updated?: string         // 最后更新时间，如 "2025-01-12"
  voices: {
    cosyvoice: TTSVoice[]
    volcano: TTSVoice[]
    elevenlabs: TTSVoice[]
    custom: TTSVoice[]
  }
  audio_formats: AudioFormat[]
}

/**
 * 语音配置 API 服务
 */
export class VoiceConfigApiService {
  /**
   * 获取 ASR 提供商列表
   * GET /api/v1/config/asr-providers
   */
  static async getASRProviders(): Promise<SuccessResponse<ASRProvidersResponse>> {
    return ApiClient.getWithClient<ASRProvidersResponse>(
      apiClients.agentAdmin,
      '/config/asr-providers'
    )
  }

  /**
   * 获取 TTS 提供商列表
   * GET /api/v1/config/tts-providers
   */
  static async getTTSProviders(): Promise<SuccessResponse<TTSProvidersResponse>> {
    return ApiClient.getWithClient<TTSProvidersResponse>(
      apiClients.agentAdmin,
      '/config/tts-providers'
    )
  }

  /**
   * 获取 TTS 音色列表
   * GET /api/v1/config/tts-voices
   */
  static async getTTSVoices(): Promise<SuccessResponse<TTSVoicesResponse>> {
    return ApiClient.getWithClient<TTSVoicesResponse>(
      apiClients.agentAdmin,
      '/config/tts-voices'
    )
  }
}

